import React from "react";
import { Form, Button, Modal, Dropdown } from "semantic-ui-react";
import { useFormik } from "formik";
import { determineEnergyUnits } from "../../../../utils/dataAndFormulas/energyForms";
import { useLanguage } from "../../../../contexts";
import "./UnitsForm.scss";

export function UnitsForm(props) {
  const { show, onClose, onSave, currentUnits, registeredFields } = props;
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  // Lista de todos los campos de energía por defecto
  const allEnergyFields = [
    { key: "electricity_standard_combined", label: t("electricity_standard_combined") },
    { key: "electricity_standard_domestic", label: t("electricity_standard_domestic") },
    { key: "electricity_standard_production", label: t("electricity_standard_production") },
    { key: "electricity_cost_total", label: t("electricity_cost_total") },
    { key: "renewable_energies_ppa_combined", label: t("renewable_energies_ppa_combined") },
    { key: "renewable_energies_ppa_domestic", label: t("renewable_energies_ppa_domestic") },
    { key: "renewable_energies_ppa_production", label: t("renewable_energies_ppa_production") },
    { key: "renewable_energies_ppa_cost", label: t("renewable_energies_ppa_cost") },
    { key: "renewable_energies_on_site_combined", label: t("renewable_energies_on_site_combined") },
    { key: "renewable_energies_on_site_domestic", label: t("renewable_energies_on_site_domestic") },
    { key: "renewable_energies_on_site_production", label: t("renewable_energies_on_site_production") },
    { key: "renewable_energies_on_site_maintenance_and_amortization", label: t("renewable_energies_on_site_maintenance_and_amortization") },
    { key: "natural_gas_combined", label: t("natural_gas_combined") },
    { key: "natural_gas_cogeneration", label: t("natural_gas_cogeneration") },
    { key: "natural_gas_production_operations", label: t("natural_gas_production_operations") },
    { key: "natural_gas_domestic", label: t("natural_gas_domestic") },
    { key: "natural_gas_total_cost", label: t("natural_gas_total_cost") },
    { key: "glp_combined", label: t("glp_combined") },
    { key: "glp_cogeneration", label: t("glp_cogeneration") },
    { key: "glp_production_operations", label: t("glp_production_operations") },
    { key: "glp_domestic", label: t("glp_domestic") },
    { key: "glp_total_cost", label: t("glp_total_cost") },
    { key: "heavy_fuel_oil_total", label: t("heavy_fuel_oil_total") },
    { key: "heavy_fuel_oil_total_cost", label: t("heavy_fuel_oil_total_cost") },
    { key: "light_fuel_oil_total", label: t("light_fuel_oil_total") },
    { key: "light_fuel_oil_total_cost", label: t("light_fuel_oil_total_cost") },
    { key: "coal_total", label: t("coal_total") },
    { key: "coal_total_cost", label: t("coal_total_cost") },
    { key: "diesel_combined", label: t("diesel_combined") },
    { key: "diesel_cogeneration", label: t("diesel_cogeneration") },
    { key: "diesel_production_operations", label: t("diesel_production_operations") },
    { key: "diesel_forklifts", label: t("diesel_forklifts") },
    { key: "diesel_total_cost", label: t("diesel_total_cost") },
    { key: "biodiesel_combined", label: t("biodiesel_combined") },
    { key: "biodiesel_cogeneration", label: t("biodiesel_cogeneration") },
    { key: "biodiesel_production_operations", label: t("biodiesel_production_operations") },
    { key: "biodiesel_forklifts", label: t("biodiesel_forklifts") },
    { key: "biodiesel_total_cost", label: t("biodiesel_total_cost") },
    { key: "gasoline_combined", label: t("gasoline_combined") },
    { key: "gasoline_cogeneration", label: t("gasoline_cogeneration") },
    { key: "gasoline_production_operations", label: t("gasoline_production_operations") },
    { key: "gasoline_forklifts", label: t("gasoline_forklifts") },
    { key: "gasoline_total_cost", label: t("gasoline_total_cost") },
    { key: "biofuel_combined", label: t("biofuel_combined") },
    { key: "biofuel_cogeneration", label: t("biofuel_cogeneration") },
    { key: "biofuel_production_operations", label: t("biofuel_production_operations") },
    { key: "biofuel_forklifts", label: t("biofuel_forklifts") },
    { key: "biofuel_total_cost", label: t("biofuel_total_cost") },
    { key: "biomass_total", label: t("biomass_total") },
    { key: "biomass_total_cost", label: t("biomass_total_cost") },
    { key: "steam_total", label: t("steam_total") },
    { key: "steam_total_cost", label: t("steam_total_cost") },
    { key: "biodiesel_renewable_percentage", label: t("biodiesel_renewable_percentage") },
    { key: "gasoline_renewable_percentage", label: t("gasoline_renewable_percentage") },
    { key: "irec_certificates", label: t("irec_certificates") },
  ];

  // Si hay registeredFields con datos, usar solo los campos activos
  // Si no hay registeredFields o está vacío, usar todos los campos por defecto
  const energyFields =
    registeredFields && registeredFields.length > 0
      ? registeredFields
          .filter((field) => field.active !== false) // Solo campos activos
          .map((field) => ({
            key: field.name,
            label: t(field.name),
            code: field.code,
          }))
      : allEnergyFields;

  const formik = useFormik({
    initialValues: energyFields.reduce((acc, field) => {
      acc[field.key] = currentUnits?.[field.key] || "";
      return acc;
    }, {}),
    enableReinitialize: true,
    onSubmit: (values) => {
      onSave(values);
      onClose();
    },
  });

  return (
    <Modal open={show} onClose={onClose} size="large" className="units-form-modal">
      <Modal.Content scrolling>
        <Form onSubmit={formik.handleSubmit}>
          <div className="units-form-modal__title">{t("configure_units")}</div>
          <div className="units-grid">
            {energyFields.map((field) => (
              <div key={field.key} className="unit-row">
                <span className="unit-label">{field.label}</span>
                <Dropdown
                  placeholder={t("select_unit")}
                  fluid
                  selection
                  search
                  clearable
                  options={determineEnergyUnits(field.key).map((unit) => ({
                    key: unit.key,
                    text: t(unit.name),
                    value: unit.value,
                  }))}
                  value={formik.values[field.key]}
                  onChange={(_, data) => formik.setFieldValue(field.key, data.value)}
                />
              </div>
            ))}
          </div>
        </Form>
      </Modal.Content>
      <Modal.Actions style={{ display: "flex", justifyContent: "center", gap: "8px" }}>
        <Button primary onClick={formik.handleSubmit}>
          {t("save")}
        </Button>
        <Button onClick={onClose} className="units-btn-cancel">{t("cancel")}</Button>
      </Modal.Actions>
    </Modal>
  );
}
